import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import {
  catchError,
  filter,
  map,
  merge,
  shareReplay,
  take,
  takeUntil,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { BaseDialog } from '../../../../components/dialog/base-dialog';
import { ConfirmHandler } from '../../../../components/dialog/dialog.model';

@Component({
  selector: 'app-signaling-block-audio-config-dialog',
  imports: [
    MatButton,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatRadioButton,
    MatRadioGroup,
    MatProgressSpinner,
    ReactiveFormsModule,
  ],
  templateUrl: './signaling-block-audio-config-dialog.component.html',
  styleUrl: './signaling-block-audio-config-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignalingBlockAudioConfigDialogComponent
  extends BaseDialog<{
    type: 'random' | 'playlist';
    onConfirm: ConfirmHandler;
  }>
  implements OnInit
{
  private readonly _fb = inject(FormBuilder);
  private readonly _dialogRef =
    inject<MatDialogRef<SignalingBlockAudioConfigDialogComponent>>(MatDialogRef);

  protected readonly form = this._fb.group({
    type: ['random'],
  });

  protected readonly options = [
    {
      value: 'random',
      displayValue: 'Willekeurig',
      description: 'Audio fragment wordt willekeurig gekozen en afgespeeld',
    },
    {
      value: 'playlist',
      displayValue: 'Afspeellijst',
      description: 'Audio fragmenten worden afspeeld in volgorde',
    },
  ];

  protected readonly submitted = signal(false);
  protected readonly loading = signal(false);

  constructor() {
    super();
  }

  ngOnInit() {
    if (this.data) {
      this.form.patchValue(this.data);
    }
  }

  override handleConfirm(): void {
    this.submitted.set(true);
    this.form.disable();

    const callback$ = this.data.onConfirm(this.form.value);
    const source$ = callback$.pipe(
      shareReplay(1),
      catchError((error) => {
        this.submitted.set(false);
        this.form.disable();

        return throwError(() => error);
      })
    );

    const loading$ = merge(
      timer(1000).pipe(
        map(() => true),
        takeUntil(source$)
      ),

      source$.pipe(map(() => false))
    );

    loading$
      .pipe(
        tap((state) => this.loading.set(state)),
        filter((state) => !state),
        tap(() => this._dialogRef.close(true)),
        take(1)
      )
      .subscribe();
  }
}
